import React, { useState, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Bot, Play, Pause, Square, Send, Loader2, Settings, Clock, Zap } from 'lucide-react';

const agentConfig = {
  website_review: { icon: '🔍', color: '#3b82f6', label: 'Website Review' },
  lead_research: { icon: '🎯', color: '#10b981', label: 'Lead Research' },
  outreach: { icon: '📤', color: '#8b5cf6', label: 'Outreach' },
  onboarding: { icon: '🚀', color: '#f59e0b', label: 'Onboarding' },
  marketing: { icon: '🎨', color: '#ec4899', label: 'Marketing' },
  operations: { icon: '⚙️', color: '#6366f1', label: 'Operations' },
  finance: { icon: '💰', color: '#14b8a6', label: 'Finance' }
};

const Agents = () => {
  const { currentProjectId, projectInfo } = useProject();
  const { api } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [taskInput, setTaskInput] = useState('');
  const [taskOutput, setTaskOutput] = useState('');
  const [runningTask, setRunningTask] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchAgents = async () => {
    if (!currentProjectId) return;
    try {
      const data = await api(`/api/agents/?project_id=${currentProjectId}`);
      setAgents(data);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [currentProjectId]);

  const toggleAgent = async (agent, action) => {
    try {
      await api(`/api/agents/${agent.agent_id}/${action}`, { method: 'POST' });
      toast.success(`Agent ${action}ed`);
      fetchAgents();
    } catch (err) {
      toast.error(`Failed to ${action} agent`);
    }
  };

  const runTask = async () => {
    if (!selectedAgent || !taskInput.trim()) return;
    
    setRunningTask(true);
    setTaskOutput('');
    
    try {
      const response = await api(`/api/agents/${selectedAgent.agent_id}/run`, {
        method: 'POST',
        body: JSON.stringify({ input: taskInput })
      });
      setTaskOutput(JSON.stringify(response, null, 2));
      toast.success('Task completed');
      fetchAgents();
    } catch (err) {
      setTaskOutput(`Error: ${err.message}`);
      toast.error(err.message);
    } finally {
      setRunningTask(false);
    }
  };

  const getTaskPlaceholder = (type) => {
    switch (type) {
      case 'website_review': return `Scan ${projectInfo.domain} and identify UX issues`;
      case 'lead_research': return 'Find 20 qualified leads in the local area';
      case 'outreach': return 'Draft introduction emails for new leads';
      case 'onboarding': return 'Analyze the signup flow for friction points';
      case 'marketing': return 'Generate 5 social media post ideas';
      case 'operations': return 'Run a health check on all systems';
      case 'finance': return 'Generate a revenue summary for this month';
      default: return 'Enter task details...';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="agents-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">AI Agents</h1>
          <p className="text-muted-foreground">
            Manage and orchestrate your autonomous AI agents
          </p>
        </div>
        <Badge variant="outline" className="w-fit">
          {agents.filter(a => a.status === 'running').length} / {agents.length} Active
        </Badge>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {agents.map((agent) => {
          const config = agentConfig[agent.agent_type] || { icon: '🤖', color: '#6b7280', label: 'Agent' };
          const isRunning = agent.status === 'running';
          const isPaused = agent.status === 'paused';
          const isError = agent.status === 'error';

          return (
            <Card 
              key={agent.agent_id}
              className={`agent-card ${agent.status} card-glow`}
              data-testid={`agent-card-${agent.agent_type}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                      style={{ backgroundColor: config.color + '20' }}
                    >
                      {config.icon}
                    </div>
                    <div>
                      <CardTitle className="text-base">{config.label}</CardTitle>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`pulse-dot ${
                          isRunning ? 'active' : isPaused ? 'warning' : isError ? 'error' : 'idle'
                        }`} />
                        <span className="text-xs text-muted-foreground capitalize">{agent.status}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <CardDescription className="text-xs line-clamp-2 mb-3">
                  {agent.description}
                </CardDescription>
                
                {agent.last_run && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                    <Clock className="w-3 h-3" />
                    Last run: {new Date(agent.last_run).toLocaleString()}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  {isRunning ? (
                    <>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => toggleAgent(agent, 'pause')}
                        data-testid={`pause-${agent.agent_type}`}
                      >
                        <Pause className="w-3 h-3 mr-1" /> Pause
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => toggleAgent(agent, 'stop')}
                        data-testid={`stop-${agent.agent_type}`}
                      >
                        <Square className="w-3 h-3" />
                      </Button>
                    </>
                  ) : (
                    <Button 
                      size="sm" 
                      onClick={() => toggleAgent(agent, 'start')}
                      data-testid={`start-${agent.agent_type}`}
                    >
                      <Play className="w-3 h-3 mr-1" /> Start
                    </Button>
                  )}
                  
                  <Dialog open={dialogOpen && selectedAgent?.agent_id === agent.agent_id} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (open) {
                      setSelectedAgent(agent);
                      setTaskInput('');
                      setTaskOutput('');
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="ghost" data-testid={`run-task-${agent.agent_type}`}>
                        <Zap className="w-3 h-3" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <span className="text-xl">{config.icon}</span>
                          Run {config.label} Task
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Task Input</label>
                          <Textarea
                            placeholder={getTaskPlaceholder(agent.agent_type)}
                            value={taskInput}
                            onChange={(e) => setTaskInput(e.target.value)}
                            rows={4}
                            data-testid="task-input"
                          />
                        </div>
                        <Button 
                          onClick={runTask}
                          disabled={runningTask || !taskInput.trim()}
                          className="w-full"
                          data-testid="execute-task-btn"
                        >
                          {runningTask ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Running...
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-2" />
                              Execute Task
                            </>
                          )}
                        </Button>
                        {taskOutput && (
                          <div>
                            <label className="text-sm font-medium mb-2 block">Output</label>
                            <pre className="p-4 bg-black/40 rounded-lg text-xs text-green-400 overflow-auto max-h-64 scrollbar-thin" data-testid="task-output">
                              {taskOutput}
                            </pre>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Agents;
